import { BrowserModule, Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GraphQLModule } from '@app/graphql.module';
import { HttpClientModule } from '@angular/common/http';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';
import { VgStreamingModule } from '@videogular/ngx-videogular/streaming';
import { IvyCarouselModule } from 'angular-responsive-carousel';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { CarouselModule } from 'primeng/carousel';
import { ApolloModule } from 'apollo-angular';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';

// Angular Fire
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAnalyticsModule } from '@angular/fire/compat/analytics';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';

// Guards
import { AuthGuard } from '@app/auth/auth.guard';
import { AnonymousGuard } from '@app/auth/anonymous.guard';
import { CanDeactivateGuard } from '@app/guards/can-deactivate.guard';

// Resolvers
import { PetInfoResolver } from '@app/resolvers/pet-info.resolver';
import { PreferredLocationResolver } from '@app/resolvers/preferred-location.resolver';
import { PaymentCardInfoResolver } from '@app/resolvers/payment-card-info.resolver';

// Services
import { DialogService } from '@app/services/dialog.service';
import { GoogleMapsService } from '@app/services/google-maps.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';

// Pages
import { WelcomePageComponent } from '@app/pages/welcome-page/welcome-page.component';
import { DogProfilePageComponent } from '@app/pages/dog-profile-page/dog-profile-page.component';
import { LandingPageComponent } from '@app/pages/landing-page/landing-page.component';
import { MyProfilePageComponent } from '@app/pages/my-profile-page/my-profile-page.component';
import { ContactDetailsPageComponent } from '@app/pages/my-profile-page/contact-details-page/contact-details-page.component';
import { RegisterPageComponent } from '@app/pages/register-page/register-page.component';
import { SignInPageComponent } from '@app/pages/sign-in-page/sign-in-page.component';
import { OtherContactsPageComponent } from '@app/pages/my-profile-page/other-contacts-page/other-contacts-page.component';
import { CamperApplicationPageComponent } from '@app/pages/my-profile-page/camper-application-page/camper-application-page.component';
import { PreferencesPageComponent } from '@app/pages/preferences-page/preferences-page.component';
import { CampLocationsPageComponent } from '@app/pages/my-profile-page/camp-locations-page/camp-locations-page.component';
import { CampDetailsDialogComponent } from '@app/components/landing/camp-details-dialog/camp-details-dialog.component';
import { BeginPageComponent } from '@app/pages/my-profile-page/begin-page/begin-page.component';
import { ChangePasswordPageComponent } from '@app/pages/change-password-page/change-password-page.component';
import { InterviewRequestPageComponent } from '@app/pages/interview-request-page/interview-request-page.component';
import { BookingsPageComponent } from '@app/pages/bookings-page/bookings-page.component';
import { BookingsDetailsPageComponent } from '@app/pages/bookings-page/bookings-details-page/bookings-details-page.component';
import { ConfirmationPageComponent } from '@app/pages/register-page/confirmation-page/confirmation-page.component';
import { ForgotPasswordPageComponent } from '@app/pages/forgot-password-page/forgot-password-page.component';
import { ReservationPageComponent } from '@app/pages/reservation-page/reservation-page.component';
import { CamperCamsPageComponent } from '@app/pages/camper-cams-page/camper-cams-page.component';
import { AddCamperPageComponent } from '@app/pages/add-camper-page/add-camper-page.component';
import { PaymentListPageComponent } from '@app/pages/payment-list-page/payment-list-page.component';
import { PaymentEditPageComponent } from '@app/pages/payment-edit-page/payment-edit-page.component';
import { PackagePageComponent } from '@app/pages/package-page/package-page.component';

// Components
import { ArrowLinkComponent } from '@app/components/_share/arrow-link/arrow-link.component';
import { ArrowLinkLeftComponent } from '@app/components/_share/arrow-link-left/arrow-link-left.component';
import { ArrowLinkRightComponent } from '@app/components/_share/arrow-link-right/arrow-link-right.component';
import { ContentHeaderComponent } from '@app/components/_share/content-header/content-header.component';
import { PasswordStrengthBarComponent } from '@app/components/_share/password-strength-bar/password-strength-bar.component';
import { ContactDetailsFormComponent } from '@app/components/_share/contact-details-form/contact-details-form.component';
import { BasicInfoComponent } from '@app/components/register/basic-info/basic-info.component';
import { PrivacyPolicyFooterComponent } from '@app/components/_share/privacy-policy-footer/privacy-policy-footer.component';
import { SignInFormComponent } from '@app/components/sign-in/sign-in-form/sign-in-form.component';
import { PasswordInputComponent } from '@app/components/_share/password-input/password-input.component';
import { ConfirmDialogComponent } from '@app/components/_share/confirm-dialog/confirm-dialog.component';
import { PasswordFormComponent } from '@app/components/_share/password-form/password-form.component';
import { LocationTileComponent } from '@app/components/_share/location-tile/location-tile.component';
import { CampLocationsListComponent } from '@app/components/my-profile/camp-locations/camp-locations-list/camp-locations-list.component';
import { EditPreferredLocationComponent } from '@app/components/my-profile/camp-locations/edit-preferred-location/edit-preferred-location.component';
import { CampLocationSearchSelectComponent } from '@app/components/_share/camp-location-search-select/camp-location-search-select.component';
import { SearchPreferredLocationComponent } from '@app/components/my-profile/camp-locations/search-preferred-location/search-preferred-location.component';
import { BinaryChoiceSelectorComponent } from '@app/components/_share/binary-choice-selector/binary-choice-selector.component';
import { CamperDetailsComponent } from '@app/components/dog-profile/camper-details/camper-details.component';
import { ChangePasswordComponent } from '@app/components/change-password/change-password.component';
import { DogRemovedSurveyComponent } from '@app/components/dog-profile/dog-removed-survey/dog-removed-survey.component';
import { HealthRecordsComponent } from '@app/components/dog-profile/health-records/health-records.component';
import { HomeAddressFormComponent } from '@app/components/my-profile/home-address-form/home-address-form.component';
import { DietMedsComponent } from '@app/components/dog-profile/diet-meds/diet-meds.component';
import { MedicationTypeSearchComponent } from '@app/components/dog-profile/diet-meds/medication-type-search/medication-type-search.component';
import { DogProfileWelcomeComponent } from '@app/components/dog-profile/dog-profile-welcome/dog-profile-welcome.component';
import { StepOneComponent } from '@app/components/interview-request/step-one/step-one.component';
import { StepTwoComponent } from '@app/components/interview-request/step-two/step-two.component';
import { PetTileFormComponent } from '@app/components/interview-request/pet-tile-form/pet-tile-form.component';
import { PetTileReviewComponent } from '@app/components/interview-request/pet-tile-review/pet-tile-review.component';
import { InterviewRequestSubmittedComponent } from '@app/components/interview-request/interview-request-submitted/interview-request-submitted.component';
import { InterviewRequestChangeLocationComponent } from '@app/components/interview-request/interview-request-change-location/interview-request-change-location.component';
import { InterviewRequestSearchLocationComponent } from '@app/components/interview-request/interview-request-search-location/interview-request-search-location.component';
import { BookingsTileComponent } from '@app/components/_share/bookings-tile/bookings-tile.component';
import { BookingsListComponent } from '@app/components/bookings-list/bookings-list.component';
import { BookingsDetailsComponent } from '@app/components/bookings-details/bookings-details.component';
import { LandingComponent } from '@app/components/landing/landing.component';
import { LandingUpcomingComponent } from '@app/components/landing/landing-upcoming/landing-upcoming.component';
import { LandingBookingsTileComponent } from '@app/components/landing/landing-upcoming/landing-bookings-tile/landing-bookings-tile.component';
import { LandingDogComponent } from '@app/components/landing/landing-dog/landing-dog.component';
import { LandingLocationComponent } from '@app/components/landing/landing-location/landing-location.component';
import { LandingLocationTileComponent } from '@app/components/landing/landing-location/landing-location-tile/landing-location-tile.component';
import { LandingMarketingComponent } from '@app/components/landing/landing-marketing/landing-marketing.component';
import { VerificationCodeInputComponent } from '@app/components/_share/verification-code-input/verification-code-input.component';
import { OneFactorAuthComponent } from '@app/components/register/one-factor-auth/one-factor-auth.component';
import { StartOverDialogComponent } from '@app/components/register/start-over-dialog/start-over-dialog.component';
import { LocationSelectionComponent } from '@app/components/register/location-selection/location-selection.component';
import { ConfirmationComponent } from '@app/components/register/confirmation/confirmation.component';
import { SignInBasicInfoComponent } from '@app/components/sign-in/sign-in-basic-info/sign-in-basic-info.component';
import { FooterComponent } from '@app/components/_share/footer/footer.component';
import { NavComponent } from '@app/components/_share/nav/nav.component';
import { ForgotPasswordEmailComponent } from '@app/components/forgot-password/forgot-password-email/forgot-password-email.component';
import { ForgotPasswordConfirmComponent } from '@app/components/forgot-password/forgot-password-confirm/forgot-password-confirm.component';
import { LoadingIndicatorDialogComponent } from '@app/components/_share/loading-indicator-dialog/loading-indicator-dialog.component';
import { PreferencesComponent } from '@app/components/preferences/preferences.component';
import { ReservationLocationComponent } from '@app/components/reservation/reservation-location/reservation-location.component';
import { VetLocationSearchSelectComponent } from '@app/components/_share/vet-location-search-select/vet-location-search-select.component';
import { SearchPreferredVetComponent } from '@app/components/dog-profile/search-preferred-vet/search-preferred-vet.component';
import { ReservationDogsComponent } from '@app/components/reservation/reservation-dogs/reservation-dogs.component';
import { ReservationAddOnsComponent } from '@app/components/reservation/reservation-add-ons/reservation-add-ons.component';
import { ReservationServicesComponent } from '@app/components/reservation/reservation-services/reservation-services.component';
import { ReservationDateComponent } from '@app/components/reservation/reservation-date/reservation-date.component';
import { ReservationPetTileFormComponent } from '@app/components/reservation/reservation-dogs/reservation-pet-tile-form/reservation-pet-tile-form.component';
import { ReservationServiceTileFormComponent } from '@app/components/reservation/reservation-services/reservation-service-tile-form/reservation-service-tile-form.component';
import { ReservationDogAddOnsComponent } from '@app/components/reservation/reservation-add-ons/reservation-dog-add-ons/reservation-dog-add-ons.component';
import { ReservationSummaryComponent } from '@app/components/reservation/reservation-summary/reservation-summary.component';
import { ReservationSummaryTileComponent } from '@app/components/reservation/reservation-summary/reservation-summary-tile/reservation-summary-tile.component';
import { ReservationThankYouComponent } from '@app/components/reservation/reservation-thank-you/reservation-thank-you.component';
import { ReservationVaccineDialogComponent } from '@app/components/reservation/reservation-summary/reservation-vaccine-dialog/reservation-vaccine-dialog.component';
import { CamperCamsComponent } from '@app/components/camper-cams/camper-cams.component';
import { CamperCamsChangeLocationComponent } from '@app/components/camper-cams/camper-cams-change-location/camper-cams-change-location.component';
import { CamperCamsSearchLocationComponent } from '@app/components/camper-cams/camper-cams-search-location/camper-cams-search-location.component';
import { PaymentListComponent } from '@app/components/payment/payment-list/payment-list.component';
import { PaymentEditComponent } from '@app/components/payment/payment-edit/payment-edit.component';
import { PaymentTileComponent } from '@app/components/_share/payment-tile/payment-tile.component';
import { PaymentDetailsFormComponent } from '@app/components/_share/payment-details-form/payment-details-form.component';
import { PackageConfirmComponent } from '@app/components/package/package-confirm/package-confirm.component';
import { PackageListComponent } from '@app/components/package/package-list/package-list.component';
import { PackageOrderSummaryComponent } from '@app/components/package/package-order-summary/package-order-summary.component';
import { PackageCheckoutComponent } from '@app/components/package/package-checkout/package-checkout.component';
import { PackageListChangeLocationComponent } from '@app/components/package/package-change-location/package-list-change-location.component';
import { PackageSearchLocationComponent } from '@app/components/package/package-search-location/package-search-location.component';
import { PackageItemComponent } from '@app/components/package/package-item/package-item.component';
import { PackagePaymentSelectionComponent } from '@app/components/package/package-payment-selection/package-payment-selection.component';
import { CamperListPageComponent } from '@app/pages/camper-list-page/camper-list-page.component';
import { CamperListComponent } from '@app/components/_share/camper-list/camper-list.component';
import { CamperTileComponent } from '@app/components/_share/camper-list/camper-tile/camper-tile.component';

// Pipes
import { PhoneNumberPipe } from '@app/pipes/phone-number.pipe';
import { CountryPipe } from '@app/pipes/country.pipe';
import { StateProvincePipe } from '@app/pipes/state-province.pipe';
import { UnitConversionPipe } from '@app/pipes/unit-conversion.pipe';
import { CurrencyPipe } from '@angular/common';

// Directives
import { MatchHeightDirective } from '@app/directives/match-height.directive';
import { NoDoubleClickDirective } from '@app/directives/no-double-click.directive';

// Helpers
import {
  AppDateAdapter,
  APP_DATE_FORMATS,
} from '@app/shared/format-datepicker';
import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';

export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

export const COMPONENTS = [
  AppComponent,
  // Pages
  WelcomePageComponent,
  DogProfilePageComponent,
  LandingPageComponent,
  MyProfilePageComponent,
  RegisterPageComponent,
  SignInPageComponent,
  ContactDetailsPageComponent,
  OtherContactsPageComponent,
  CamperApplicationPageComponent,
  PreferencesPageComponent,
  CampLocationsPageComponent,
  BeginPageComponent,
  ChangePasswordPageComponent,
  InterviewRequestPageComponent,
  ReservationPageComponent,
  BookingsPageComponent,
  BookingsDetailsPageComponent,
  ConfirmationPageComponent,
  ForgotPasswordPageComponent,
  AddCamperPageComponent,
  PaymentListPageComponent,
  PaymentEditPageComponent,
  PackagePageComponent,

  // Components
  LandingComponent,
  ArrowLinkComponent,
  ArrowLinkRightComponent,
  ArrowLinkLeftComponent,
  ContentHeaderComponent,
  PasswordStrengthBarComponent,
  ContactDetailsFormComponent,
  BasicInfoComponent,
  PrivacyPolicyFooterComponent,
  SignInFormComponent,
  PasswordInputComponent,
  ConfirmDialogComponent,
  PasswordFormComponent,
  PasswordFormComponent,
  LocationTileComponent,
  CampDetailsDialogComponent,
  CampLocationsListComponent,
  EditPreferredLocationComponent,
  CampLocationSearchSelectComponent,
  VetLocationSearchSelectComponent,
  SearchPreferredLocationComponent,
  SearchPreferredVetComponent,
  BinaryChoiceSelectorComponent,
  CamperDetailsComponent,
  ChangePasswordComponent,
  DogRemovedSurveyComponent,
  HealthRecordsComponent,
  HomeAddressFormComponent,
  DietMedsComponent,
  MedicationTypeSearchComponent,
  DogProfileWelcomeComponent,
  StepOneComponent,
  StepTwoComponent,
  PetTileFormComponent,
  PetTileReviewComponent,
  InterviewRequestSubmittedComponent,
  InterviewRequestChangeLocationComponent,
  InterviewRequestSearchLocationComponent,
  BookingsTileComponent,
  BookingsListComponent,
  BookingsDetailsComponent,
  LandingUpcomingComponent,
  LandingBookingsTileComponent,
  LandingDogComponent,
  LandingLocationComponent,
  LandingLocationTileComponent,
  LandingMarketingComponent,
  VerificationCodeInputComponent,
  OneFactorAuthComponent,
  StartOverDialogComponent,
  LocationSelectionComponent,
  ConfirmationComponent,
  SignInBasicInfoComponent,
  FooterComponent,
  NavComponent,
  ForgotPasswordEmailComponent,
  ForgotPasswordConfirmComponent,
  LoadingIndicatorDialogComponent,
  PreferencesComponent,
  ReservationLocationComponent,
  ReservationDogsComponent,
  ReservationAddOnsComponent,
  ReservationServicesComponent,
  ReservationDateComponent,
  ReservationPetTileFormComponent,
  ReservationServiceTileFormComponent,
  ReservationDogAddOnsComponent,
  ReservationSummaryComponent,
  ReservationSummaryTileComponent,
  ReservationThankYouComponent,
  ReservationVaccineDialogComponent,
  CamperCamsPageComponent,
  CamperCamsComponent,
  CamperCamsChangeLocationComponent,
  CamperCamsSearchLocationComponent,
  PaymentListComponent,
  PaymentEditComponent,
  PaymentTileComponent,
  PaymentDetailsFormComponent,
  PackagePageComponent,
  PackageCheckoutComponent,
  PackageListChangeLocationComponent,
  PackageListComponent,
  PackageConfirmComponent,
  PackageOrderSummaryComponent,
  PackageSearchLocationComponent,
  PackageItemComponent,
  PackagePaymentSelectionComponent,
  CamperListPageComponent,
  CamperListComponent,
  CamperTileComponent,

  // Pipes
  PhoneNumberPipe,
  CountryPipe,
  StateProvincePipe,
  UnitConversionPipe,
  // Directives
  MatchHeightDirective,
  NoDoubleClickDirective,
];

export const PROVIDERS = [
  // Guards
  AuthGuard,
  AnonymousGuard,
  CanDeactivateGuard,
  // Resolvers
  PetInfoResolver,
  PreferredLocationResolver,
  PaymentCardInfoResolver,
  // Pipes
  UnitConversionPipe,
  CurrencyPipe,
  // Services
  GoogleMapsService,
  DialogService,
  LoadingIndicatorService,
  Title,
  // Date configs
  { provide: DateAdapter, useClass: AppDateAdapter },
  { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
  { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
  // Sentry
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => () => {},
    deps: [Sentry.TraceService],
    multi: true,
  },
];

@NgModule({
  declarations: COMPONENTS,
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    GraphQLModule,
    HttpClientModule,
    MatTabsModule,
    MatDialogModule,
    MatSnackBarModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatSelectModule,
    MatInputModule,
    MatProgressSpinnerModule,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    VgStreamingModule,
    IvyCarouselModule,
    AngularFireModule.initializeApp(environment.firebaseConfig), // firebase setup
    AngularFireAnalyticsModule,
    AngularFirestoreModule,
    NgxMaskModule.forRoot(),
    CarouselModule,
    ApolloModule,
  ],
  providers: PROVIDERS,
  bootstrap: [AppComponent],
})
export class AppModule {}
